import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import VideoGate from "@/components/VideoGate";
import Hero from "@/components/Hero";
import EventSection from "@/components/EventSection";
import ChallengesSection from "@/components/ChallengesSection";
import WinnersSection from "@/components/WinnersSection";
import ProgramTimeline from "@/components/ProgramTimeline";
import Countdown from "@/components/Countdown";
import RegistrationForm from "@/components/RegistrationForm";
import ConfirmationScreen from "@/components/ConfirmationScreen";
import Footer from "@/components/Footer";
import { LogoRea, LogoPartners } from "@/components/Logos";

export default function ExperiencePage() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [gate, setGate] = useState(sessionStorage.getItem("video_seen") !== "1");
  const [preselect, setPreselect] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const selectTournament = (t) => {
    setPreselect({ t, ts: Date.now() });
    setTimeout(scrollToForm, 100);
  };

  const onGateComplete = () => setGate(false);
  const replayVideo = () => {
    setConfirmed(null);
    setGate(true);
    window.scrollTo({ top: 0 });
  };

  if (!settings) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#050505]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-[#f5c542]" />
      </div>
    );
  }

  return (
    <div className="relative">
      <AnimatePresence>
        {gate && <VideoGate settings={settings} onComplete={onGateComplete} />}
      </AnimatePresence>

      {/* Sticky header */}
      <header
        className={`fixed inset-x-0 top-0 z-40 border-b transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled || confirmed
            ? "border-white/5 bg-[#050505]/80 backdrop-blur-md"
            : "border-transparent bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 md:px-10">
          <LogoPartners src={settings.logo_partners} size="h-9 md:h-10" />
          <LogoRea src={settings.logo_rea} size="h-12 md:h-14" />
        </div>
      </header>

      {confirmed ? (
        <div className="pt-20">
          <ConfirmationScreen registration={confirmed} settings={settings} onReplayVideo={replayVideo} />
          <Footer settings={settings} />
        </div>
      ) : (
        <main>
          <Hero settings={settings} onParticipate={scrollToForm} />
          <EventSection />
          <ChallengesSection stats={stats} onSelectTournament={selectTournament} />
          <WinnersSection winners={settings.winners} />
          <ProgramTimeline program={settings.program} />
          <Countdown targetIso={settings.event_date_iso} />
          <div ref={formRef}>
            <RegistrationForm
              settings={settings}
              stats={stats}
              preselect={preselect?.t}
              onSuccess={(reg) => {
                setConfirmed(reg);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </div>
          <Footer settings={settings} />
        </main>
      )}
    </div>
  );
}
